import React, { useState, useEffect } from "react";
import Card from "../../components/ui/Card";
import InputWithIcon from "../../components/ui/InputWithIcon";
import Button from "../../components/ui/Button";
import api from "../../api/api";

const OpeningBalanceEdit = ({ setView, editingContext }) => {
    const [formData, setFormData] = useState({
        CPId: "",
        Vid: "",
        VCode: "",
        Vname: "",
        vendor: "",
        invNo: "",
        invDate: "",
        amount: "",
        notes: "",
    });
    const [loading, setLoading] = useState(true);

    // ✅ Fetch full data from Edit_OpenningBalance API
    useEffect(() => {
        const fetchEditData = async () => {
            if (!editingContext?.id) return;

            try {
                setLoading(true);
                const response = await api.Edit_OpenningBalance(editingContext.id);

                const record =
                    response?.Edit_OpenningBalanceAPIjs?.Table?.[0] ||
                    response?.Table?.[0];

                if (record) {
                    setFormData({
                        CPId: record.CPId,
                        Vid: record.Vid,
                        VCode: record.VCode,
                        Vname: record.Party,
                        vendor: `${record.Party} (${record.VCode})`,
                        invNo: record.InvNo || "",
                        invDate: record.InvDate ? record.InvDate.split("T")[0] : "",
                        amount: record.NetTotal || "",
                        notes: record.Notes || "",
                    });
                }
            } catch (err) {
                console.error("Error fetching edit data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchEditData();
    }, [editingContext]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    // ✅ Submit Update
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                SqlAction: "update",
                Cpid: formData.CPId,
                Vid: formData.Vid,
                Vcode: formData.VCode,
                Vname: formData.Vname,
                Invno: formData.invNo,
                Invdate: new Date(formData.invDate).toISOString(),
                NetAmt: Number(formData.amount),
                Notes: formData.notes,
            };

            console.log("Submitting update payload:", payload);

            const response = await api.SaveOpenningBalance(payload);
            console.log("Update Response:", response);

            const table =
                response?.SaveOpenningBalanceAPIjs?.Table ||
                response?.Table ||
                [];

            if (table.length > 0) {
                const firstRow = table[0];
                if (firstRow.Error) {
                    alert(`Update failed: ${firstRow.Error}`);
                } else if (firstRow.Column1) {
                    alert("Record updated successfully!");
                    setView("openingbalancelist"); // ✅ Go back after update
                } else {
                    alert("Update failed. Unexpected response from server.");
                }
            } else {
                alert("Update failed. No response from server.");
            }
        } catch (err) {
            console.error("Update failed:", err);
            alert("Error updating opening balance.");
        }
    };

    if (loading) {
        return <p className="text-center text-gray-500 py-6">Loading record...</p>;
    }

    return (
        <div className="w-full px-0 sm:max-w-4xl sm:mx-0 sm:pr-4">
            <Card title="Edit Opening Balance" className="w-full px-0 sm:px-6">
                <form onSubmit={handleSubmit} className="w-full space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        {/* Vendor (readonly) */}
                        <div>
                            <label
                                htmlFor="vendor"
                                className="block mb-1 font-semibold text-gray-700"
                            >
                                Party
                            </label>
                            <input
                                type="text"
                                id="vendor"
                                value={formData.vendor}
                                readOnly
                                className="w-full p-2 border border-gray-300 bg-gray-100 rounded-md"
                            />
                        </div>

                        {/* Invoice No */}
                        <div>
                            <label
                                htmlFor="invNo"
                                className="block mb-1 font-semibold text-gray-700"
                            >
                                Invoice No*
                            </label>
                            <input
                                type="text"
                                id="invNo"
                                value={formData.invNo}
                                onChange={handleChange}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter Invoice No"
                                required
                            />
                        </div>

                        {/* Invoice Date */}
                        <div>
                            <label
                                htmlFor="invDate"
                                className="block mb-1 font-semibold text-gray-700"
                            >
                                Invoice Date*
                            </label>
                            <input
                                type="date"
                                id="invDate"
                                value={formData.invDate}
                                onChange={handleChange}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>

                        {/* Amount */}
                        <div>
                            <label
                                htmlFor="amount"
                                className="block mb-1 font-semibold text-gray-700"
                            >
                                Amount*
                            </label>
                            <input
                                type="number"
                                id="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label
                            htmlFor="notes"
                            className="block mb-1 font-semibold text-gray-700"
                        >
                            Notes
                        </label>
                        <textarea
                            id="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows={3}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter notes"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end gap-4 pt-6 mt-8 border-t">
                        <Button
                            type="submit"
                            className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
                        >
                            Update
                        </Button>
                        
                        <Button
                            type="button"
                            onClick={() => setView("openingbalancelist")} // ✅ Back to list
                            className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
                        >
                            Close
                        </Button>
                        
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default OpeningBalanceEdit;
